import React, { useMemo, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faCheck,
    faTimes,
    faTruck,
    faFileAlt,
    faEnvelope,
    faComment,
    faHistory,
    faEdit,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { USER_ROLES, AUDIT_LOG_VIEWER_ROLES } from '../../utils/constants';
import { useRole } from '../../context/roleContext';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';
import { useMessageAlert } from '../common/MessageAlert';
import ConfirmDialog from '../common/ConfirmDialog';
import AuditLogsModal from '../common/AuditLogsModal';
import { useChannelSettings } from '../../context/ChannelSettingsContext';
import { canEditChallanRecord, editDeniedReason } from '../../utils/challanRbac';

const OrderStatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const s = (status || '').toString().toLowerCase();

    const getLabel = (key) => {
        if (!key) return '';
        const k = key.toLowerCase();
        return t(`deliveryChallanModule.actions.status.${k}`) || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const badgeStyle = (icon, color, label) => (
        <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={icon} className="text-sm" style={{ color }} />
            <span className="text-sm font-medium" style={{ color }}>{label}</span>
        </div>
    );

    if (s === 'delivered') return badgeStyle(faCheck, UI_COLORS.STATUS.SUCCESS, getLabel(s));
    if (s === 'in_transit') return badgeStyle(faTruck, UI_COLORS.STATUS.INFO, getLabel(s));

    return badgeStyle(faTimes, UI_COLORS.TEXT.MUTED, getLabel(s));
};

// Roles that have ever had an edit-challan button here. The challan rule decides
// whether it is enabled; this decides whether it is shown at all, so roles that were
// never offered the action do not start seeing a greyed-out one.
const EDIT_CHALLAN_ROLES = [USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN];

const DeliveryChallanTable = ({
    orders = [],
    loading = false,
    onViewOrder,
    onMarkDelivered,
    onPrintChallan,
    onSendChallanNotification,
    onEditChallan,
    onRefresh,
}) => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useMessageAlert();
    const { userRole } = useRole();

    // Shared rule (src/utils/challanRbac.js), mirroring app/utils/challan_rbac.py.
    // A row on this screen always carries a challan, so this is Super Admin only and
    // only on the challan's own IST day — which is also what blocks back-dated
    // challans for every role.
    const canEditRow = useCallback((row) => canEditChallanRecord({
        userRole,
        challanGenerated: row?.challan_generated ?? true,
        challanDate: row?.challan_date,
    }), [userRole]);

    // Blocked by the challan rule -> keep the button visible but disabled with the
    // reason; a silently missing button reads as a broken screen.
    const editReasonRow = useCallback((row) => editDeniedReason({
        userRole,
        challanGenerated: row?.challan_generated ?? true,
        challanDate: row?.challan_date,
    }) || null, [userRole]);

    const { emailEnabled, whatsappEnabled } = useChannelSettings();
    const [actionLoading, setActionLoading] = useState(false);
    const [auditLogTarget, setAuditLogTarget] = useState(null);

    const [showConfirmDeliver, setShowConfirmDeliver] = useState(false);
    const [confirmId, setConfirmId] = useState(null);

    const [showSendChallanEmailConfirm, setShowSendChallanEmailConfirm] = useState(false);
    const [sendChallanEmailTargetId, setSendChallanEmailTargetId] = useState(null);
    const [pendingChannel, setPendingChannel] = useState('email');

    const openSendDialog = useCallback((id, channel) => {
        setPendingChannel(channel);
        setSendChallanEmailTargetId(id);
        setShowSendChallanEmailConfirm(true);
    }, []);

    const handleAction = useCallback(async (actionFn, id, successMsg, failMsg) => {
        setActionLoading(true);
        try {
            const res = await actionFn(id, {});
            if (res && res.success) {
                showSuccess(successMsg);
                if (onRefresh) onRefresh();
            } else {
                showError(failMsg + (res?.error ? `: ${res.error}` : ''));
            }
        } catch (e) {
            showError(failMsg);
        } finally {
            setActionLoading(false);
        }
    }, [showSuccess, showError, onRefresh]);

    const columns = useMemo(() => [
        {
            id: 'challan_number',
            label: t('deliveryChallanModule.table.columns.challanNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.challan_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.challan_number || '-'}
                </div>
            ),
        },
        {
            id: 'sub_order_number',
            label: t('deliveryChallanModule.table.columns.subOrderNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.sub_order_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.sub_order_number || '-'}
                </div>
            ),
        },
        {
            id: 'tally_name',
            label: t('deliveryChallanModule.table.columns.tallyName'),
            sortable: true,
            sortAccessor: (o) => (o?.tally_name || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.tally_name || '-'}
                </div>
            ),
        },
        {
            id: 'location_name',
            label: t('deliveryChallanModule.table.columns.location'),
            sortable: true,
            sortAccessor: (o) => (o?.location_name || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {o.location_name || '-'}
                </div>
            ),
        },
        {
            id: 'vehicle_number',
            label: t('commonCols.vehicleNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.vehicle_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.vehicle_number || '-'}
                </div>
            ),
        },
        {
            id: 'total_quantity',
            label: t('deliveryChallanModule.table.columns.totalQty'),
            sortable: true,
            sortAccessor: (o) => o?.total_delivered_quantity || o?.total_quantity || 0,
            renderCell: (o) => (
                <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.total_delivered_quantity || o.total_quantity} {o.unit_type}
                </span>
            ),
        },
        {
            id: 'order_remark',
            label: t('commonCols.orderRemarks'),
            sortable: true,
            sortAccessor: (o) => (o?.order_remark || '').toLowerCase(),
            renderCell: (o) => {
                if (!o.order_remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>-</span>;
                return (
                    <div 
                        className="text-[11px] font-medium break-words leading-relaxed min-w-[160px] max-w-[180px] px-2 py-1.5 rounded border" 
                        title={o.order_remark} 
                        style={{ 
                            backgroundColor: 'rgba(254, 243, 199, 0.5)', 
                            color: '#92400e', 
                            borderColor: 'rgba(217, 119, 6, 0.2)',
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {o.order_remark}
                    </div>
                );
            },
        },
        {
            id: 'status',
            label: t('deliveryChallanModule.table.columns.status'),
            sortable: true,
            align: 'center',
            renderCell: (o) => <OrderStatusBadge status={o.status} />,
        },
        {
            id: 'status_actions',
            label: t('commonCols.updateStatus'),
            align: 'center',
            renderCell: (o) => {
                const statusStr = (o?.status || '').toLowerCase();

                return (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {/* Print Challan */}
                        {o.challan_generated && !o.challan_printed && (
                            <button
                                onClick={() => onPrintChallan && onPrintChallan(o)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                                title={t('deliveryChallans.table.tooltips.printChallan')}
                            >
                                <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryChallans.table.tooltips.printChallan')}</span>
                            </button>
                        )}

                        {/* Send Challan Notification */}
                        {o.challan_generated && emailEnabled && (
                            <button
                                onClick={() => openSendDialog(o.id, 'email')}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
                                title={t('deliveryChallans.table.tooltips.sendChallanEmail')}
                            >
                                <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                                <span className="text-xs font-medium">Email</span>
                            </button>
                        )}
                        {o.challan_generated && whatsappEnabled && (
                            <button
                                onClick={() => openSendDialog(o.id, 'whatsapp')}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ color: '#ffffff', backgroundColor: '#25D366' }}
                                title={t('deliveryChallans.table.tooltips.sendChallanWhatsApp')}
                            >
                                <FontAwesomeIcon icon={faComment} className="text-xs" />
                                <span className="text-xs font-medium">WhatsApp</span>
                            </button>
                        )}

                        {/* Mark Delivered (IN_TRANSIT -> DELIVERED) */}
                        {statusStr === 'in_transit' && (
                            <button
                                onClick={() => {
                                    setConfirmId(o.id);
                                    setShowConfirmDeliver(true);
                                }}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.08) }}
                                title={t('deliveryChallanModule.actions.markDelivered')}
                            >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryChallanModule.actions.markDelivered')}</span>
                            </button>
                        )}
                    </div>
                );
            }
        },
        {
            id: 'actions',
            label: t('deliveryChallanModule.table.columns.actions'),
            align: 'right',
            renderCell: (o) => (
                <div className="flex items-center justify-end space-x-2">
                    <button
                        onClick={() => onViewOrder && onViewOrder(o)}
                        className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                        title={t('deliveryChallanModule.table.actions.view')}
                        style={{ color: UI_COLORS.TEXT.PRIMARY }}
                    >
                        <FontAwesomeIcon icon={faEye} />
                    </button>

                    {AUDIT_LOG_VIEWER_ROLES.includes(userRole) && (
                        <button
                            onClick={() => setAuditLogTarget({ id: o.order_id, label: o.order_number })}
                            className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                            title={t('deliveryChallans.table.tooltips.auditLogs')}
                            style={{ color: UI_COLORS.STATUS.INFO }}
                        >
                            <FontAwesomeIcon icon={faHistory} />
                        </button>
                    )}

                    {/* Every row here IS a challan, so the shared rule reduces to
                        Super Admin editing on the challan's own IST day. ADMIN used to
                        appear on this button and would now be refused by the API. */}
                    {EDIT_CHALLAN_ROLES.includes(userRole) && !canEditRow(o) && editReasonRow(o) && (
                        <button
                            disabled
                            className="p-1.5 rounded-md opacity-40 cursor-not-allowed"
                            title={editReasonRow(o)}
                            style={{ color: UI_COLORS.STATUS.WARNING }}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}

                    {canEditRow(o) && (
                        <button
                            onClick={() => onEditChallan && onEditChallan(o)}
                            className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                            title={t('deliveryChallanModule.actions.editChallan')}
                            style={{ color: UI_COLORS.STATUS.WARNING }}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                </div>
            ),
        },
    ], [onViewOrder, actionLoading, onPrintChallan, userRole, onEditChallan, emailEnabled, whatsappEnabled, openSendDialog, canEditRow, editReasonRow]);

    return (
        <>
            <DataTable
                columns={columns}
                data={orders}
                loading={loading || actionLoading}
                keyField="id"
                enablePagination={false}
                defaultSort={{ field: 'updated_at', direction: 'desc' }}
                emptyState={{
                    title: t('deliveryChallanModule.table.empty.title'),
                    message: t('deliveryChallanModule.table.empty.message'),
                }}
            />

            <ConfirmDialog
                isOpen={showConfirmDeliver}
                title={t('deliveryChallanModule.actions.markDelivered')}
                message={t('deliveryChallanModule.actions.confirmMarkDelivered')}
                confirmLabel={t('deliveryChallans.actions.confirmLabels.yesDelivered')}
                confirmVariant="success"
                loading={actionLoading}
                onCancel={() => setShowConfirmDeliver(false)}
                onConfirm={() => {
                    setShowConfirmDeliver(false);
                    if (confirmId) {
                        handleAction(
                            onMarkDelivered,
                            confirmId,
                            t('deliveryChallanModule.actions.markDeliveredSuccess'),
                            t('deliveryChallanModule.actions.markDeliveredFailed')
                        );
                    }
                }}
            />

            <ConfirmDialog
                isOpen={showSendChallanEmailConfirm}
                title={`Send Challan via ${pendingChannel === 'whatsapp' ? 'WhatsApp' : 'Email'}`}
                message={t('deliveryChallanModule.actions.sendChallanEmailConfirm')}
                confirmLabel={t('deliveryChallanModule.common.buttons.sendChallanEmail')}
                loadingLabel={t('deliveryChallanModule.common.buttons.sendingEmail')}
                confirmVariant="primary"
                loading={actionLoading}
                onCancel={() => setShowSendChallanEmailConfirm(false)}
                onConfirm={async () => {
                    setShowSendChallanEmailConfirm(false);
                    if (sendChallanEmailTargetId) {
                        setActionLoading(true);
                        try {
                            const res = await onSendChallanNotification(sendChallanEmailTargetId, pendingChannel);
                            if (res && res.success) {
                                showSuccess(pendingChannel === 'whatsapp' ? 'WhatsApp sent successfully' : t('deliveryChallanModule.actions.sendChallanEmailSuccess'));
                                if (onRefresh) onRefresh();
                            } else {
                                showError(res?.error || t('deliveryChallanModule.actions.sendChallanEmailFailed'));
                            }
                        } catch {
                            showError(t('deliveryChallanModule.actions.sendChallanEmailFailed'));
                        } finally {
                            setActionLoading(false);
                            setSendChallanEmailTargetId(null);
                        }
                    }
                }}
            />

            {auditLogTarget && (
                <AuditLogsModal
                    isOpen={!!auditLogTarget}
                    onClose={() => setAuditLogTarget(null)}
                    entityType="order"
                    entityId={auditLogTarget.id}
                    entityLabel={auditLogTarget.label}
                    hasTimeline={true}
                />
            )}
        </>
    );
};

export default DeliveryChallanTable;
